/**
 * `/app/premium` — the paywall, rendered from `GET /billing/checkout-config`.
 *
 * Every number on this page now arrives from the server: the plans and their
 * amounts (resolved from the live Stripe price, so a price experiment needs no
 * client deploy), the trial length, and whether this person may be sold
 * anything at all. What the client used to decide, and no longer does:
 *
 * - **which price** — it posts a `plan_id`, never a price id. The
 *   `VITE_STRIPE_*` price ids are gone from this path.
 * - **how many free days** — `trial_days` is server-evaluated and re-evaluated
 *   at creation. `?7daytrial` is deleted: it set the trial length from a URL
 *   parameter, so anyone could type themselves a free trial.
 * - **which Stripe.js call to make** — the subscribe response names it; the
 *   client must not infer it from whether a trial was offered.
 *
 * `blocked_reason` is the fourth: a subscriber who lands here is told why they
 * cannot buy rather than shown a card form.
 *
 * @see docs/projects/active/refactor-payments/refactor-everything-payments.md — `GET /billing/checkout-config`
 */
import type { CheckoutPlan } from '@listening/api-contract/schemas';
import { cn, useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import * as m from '@/paraglide/messages';

import { Button } from '@listening/frontend/components/radix/button';
import { FormRootMessage } from '@listening/frontend/components/radix/form';
import { ScrollArea } from '@listening/frontend/components/radix/scroll-area';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useEffect } from 'react';
import { Skeleton } from '@listening/frontend/components/radix/skeleton';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { Spinner } from '@/components/shared-ui/spinner';
import { useTheme } from '@/components/shared-ui/theme-provider';
import { CheckoutForm } from '@/components/web-fe/features/stripe-checkout/check-out-form';
import { PlanOptions } from '@/components/web-fe/features/stripe-checkout/plan-options';
import { StripeUnavailableNotice } from '@/components/web-fe/features/stripe-checkout/stripe-unavailable-notice';
import { AnimatedHeading } from '@/components/web-fe/misc/animated-heading';
import { ButtonLink } from '@/components/web-fe/misc/button-link';
import { ErrorComponent } from '@/components/shared-ui/error-component';
import { useLogoutAndClearCacheMutation } from '@/hooks/login-logout.hook';
import { useFreePlan } from '@/hooks/use-free-plan-availability';
import {
  billingIntervalFor,
  useBillingPeriod,
} from '@/lib/checkout/billing-period';
import { stripeCheckoutOptions } from '@/lib/checkout/checkout-lib';
import {
  blockedNoticeCopy,
  type CheckoutOffer,
  offerFor,
} from '@/lib/checkout/checkout-offer';
import { stripePromise } from '@/lib/stripe-utils';
import { useAnalytics } from '@listening/frontend/analytics/client';
import { usePaywallShownEvent } from '@/hooks/paywall-shown-event.hook';
import { bouncedFromPaywallFor } from '@/services/subscription-gate';
import {
  useBillingStatusQuery,
  useCheckoutConfigQuery,
} from '@listening/frontend/query/user-hooks';
import { useIsUserLoggedIn, useNameAndEmail } from '@/services/user-service';

// No search validation: the stale campaign params (`?promo=`, `?special-offer=`,
// `?7daytrial=`) that the retired hand-rolled checkouts steered on ride along in
// the raw search untouched, nothing reads them, and the link lands on the
// ordinary paywall.
export const Route = createFileRoute('/app/_authenticated/premium')({
  component: Page,
});

function Page() {
  const userLoggedIn = useIsUserLoggedIn();
  const statusQuery = useBillingStatusQuery({ enabled: !!userLoggedIn });

  if (bouncedFromPaywallFor(statusQuery)) return <Navigate to='/app/home' />;

  return <Checkout />;
}

function Checkout() {
  const configQuery = useCheckoutConfigQuery({ surface: 'paywall' });
  const billingPeriod = useBillingPeriod();
  usePaywallShownEvent();

  if (configQuery.isPending) return <CheckoutPageSkeleton />;
  if (configQuery.isError) return <CheckoutUnavailable />;

  const offer = offerFor(
    configQuery.data,
    undefined,
    billingIntervalFor(billingPeriod),
  );
  if (offer.kind === 'unavailable') return <CheckoutUnavailable />;
  if (offer.kind === 'blocked') {
    return (
      <CheckoutShell>
        <BlockedNotice reason={offer.reason} />
      </CheckoutShell>
    );
  }

  return (
    <CheckoutShell>
      <CheckoutElements amountCents={offer.selected.amount_cents}>
        <CheckoutForm
          formClassName='mr-0 flex w-full max-w-[1296px] flex-[0_auto] flex-row flex-wrap items-start justify-center gap-8 lg:flex-nowrap'
          initialPlan={offer.selected}
          key={offer.selected.plan_id}
        >
          <CheckoutCard>
            <FormContent
              planOptions={offer.plans}
              trialDays={offer.trialDays}
            />
          </CheckoutCard>
        </CheckoutForm>
      </CheckoutElements>
    </CheckoutShell>
  );
}

/** The plans failed to load, or resolved to nothing sellable. */
function CheckoutUnavailable() {
  return (
    <CheckoutShell>
      <ErrorComponent
        header=''
        message={m.premium_offer_failed()}
        className=''
      />
    </CheckoutShell>
  );
}

function CheckoutShell({ children }: React.PropsWithChildren) {
  const { isAvailable: isFreePlanAvailable } = useFreePlan();
  const allowSkipToHomepage = isFreePlanAvailable;

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <AnimatedHeading
        allowSkipToFree={allowSkipToHomepage}
        currentStep={0}
        pages={[
          { label: m.premium_step_plan() },
          { label: m.premium_step_purchase() },
        ]}
        showUserMenu
      />
      <ScrollArea className='grow'>
        <div className='flex justify-center p-10 md:p-20'>
          <div className='flex w-full max-w-[1296px] flex-wrap items-start justify-center gap-8'>
            <section className='min-w-[376px] flex-1 grow'>{children}</section>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

function CheckoutCard({ children }: React.PropsWithChildren) {
  return (
    <div className='border-figma-bg600 flex w-full max-w-[632px] flex-col gap-6 rounded-xl border-2 p-6 dark:border-none dark:bg-navy-440'>
      {children}
    </div>
  );
}

/**
 * `<Elements>` in deferred-intent mode, told the real amount.
 *
 * The amount decides which payment methods Stripe offers (several have
 * thresholds), and it used to be hardcoded to `0` because the page could not
 * know it. It can now.
 */
function CheckoutElements({
  amountCents,
  children,
}: React.PropsWithChildren<{ amountCents: number }>) {
  const { isDark } = useTheme();

  return (
    <Elements
      options={{
        ...stripeCheckoutOptions,
        // Restated so the options narrow to the subscription variant, which is
        // the only one carrying `amount`.
        mode: 'subscription',
        amount: amountCents,
        appearance: {
          ...stripeCheckoutOptions.appearance,
          theme: isDark ? 'night' : 'stripe',
        },
      }}
      stripe={stripePromise}
    >
      {children}
    </Elements>
  );
}

/**
 * Why this person cannot buy here — the server's `blocked_reason` reaching the
 * UI.
 *
 * The client does not work this out from provider fields; the server names one
 * of the reasons and `blockedNoticeCopy` maps it to copy, for this page and the
 * onboarding paywall alike.
 */
function BlockedNotice({
  reason,
}: {
  reason: Extract<CheckoutOffer, { kind: 'blocked' }>['reason'];
}) {
  return (
    <CheckoutCard>
      <h2
        className='text-2xl font-semibold text-foreground'
        data-paywall-blocked={reason}
      >
        {blockedNoticeCopy(reason).title}
      </h2>
      <p className='text-base text-steel-700 dark:text-steel-260'>
        {blockedNoticeCopy(reason).body}
      </p>
      <ButtonLink className='self-start rounded-full' to='/app/plan'>
        {m.plan_manage()}
      </ButtonLink>
    </CheckoutCard>
  );
}

function FormContent({
  planOptions,
  trialDays,
}: {
  planOptions: readonly CheckoutPlan[];
  trialDays: number;
}) {
  const context = useContextAndErrorIfNull(CheckoutForm.Context);
  const logout = useLogoutAndClearCacheMutation();
  const { mutation } = context;
  const [currentPlan, setCurrentPlan] = context.currentPlanState;
  // null until js.stripe.com resolves, and forever if it was blocked. Without
  // this the submit button is live before Stripe exists and `check-out-form`
  // throws `Stripe not initialized`.
  const stripe = useStripe();
  const elements = useElements();

  // `<Elements>` is mounted with the initially-selected plan's amount and never
  // re-created, so the plan switcher must follow through here — the amount is
  // what wallet sheets (Apple Pay / Google Pay) display and what deferred-intent
  // confirmation validates against.
  useEffect(() => {
    void elements?.update({ amount: currentPlan.amount_cents });
  }, [elements, currentPlan.amount_cents]);

  const { isAvailable: isFreePlanAvailable } = useFreePlan();
  const allowSkipToHomepage = isFreePlanAvailable;
  const { capture } = useAnalytics();
  const { email } = useNameAndEmail();
  return (
    <>
      <div className='flex flex-col gap-6'>
        {planOptions.length > 1 && (
          <PlanOptions
            onSelect={setCurrentPlan}
            plans={planOptions}
            selectedPlanId={currentPlan.plan_id}
            trialDays={trialDays}
          />
        )}

        <StripeUnavailableNotice />
        <PaymentElement
          options={{
            defaultValues: {
              billingDetails: email === undefined ? {} : { email },
            },
          }}
        />
      </div>
      <FormRootMessage
        className='text-center text-destructive'
        data-checkout-error
      />
      <Button
        className={cn(
          'h-auto w-full self-center rounded-full border-b-4 border-cloud-375 px-8 py-3 text-lg font-[500]',
          mutation.isPending
            ? 'bg-steel-700 disabled:cursor-not-allowed'
            : 'bg-primary',
        )}
        data-checkout-submit
        disabled={mutation.isPending || !stripe}
        onClick={() => {
          capture('premium_paywall_interaction', {
            interaction: 'submit_clicked',
          });
        }}
        type='submit'
      >
        {mutation.isPending && <Spinner className='mr-2' />}
        {m.premium_get_started()}
      </Button>
      {allowSkipToHomepage ? (
        <div className='flex justify-center'>
          <ButtonLink
            className='h-4 text-steel-700'
            onClick={() => {
              capture('premium_paywall_interaction', {
                interaction: 'skipped',
              });
            }}
            to='/app/home'
            variant='link'
          >
            {m.premium_skip()}
          </ButtonLink>
        </div>
      ) : (
        <div className='flex justify-center'>
          <Button
            className='h-4 text-steel-700'
            onClick={() => {
              capture('premium_paywall_interaction', {
                interaction: 'logout',
              });
              logout.mutate();
            }}
            variant='link'
          >
            {m.premium_logout()}
          </Button>
        </div>
      )}
    </>
  );
}

function CheckoutPageSkeleton() {
  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <div className='flex items-center justify-between border-b px-6 py-4'>
        <Skeleton className='h-5 w-32' />
        <Skeleton className='size-8 rounded-full' />
      </div>

      <div className='flex grow justify-center p-10 md:p-20'>
        <div className='flex w-full max-w-[1296px] flex-wrap items-start justify-center gap-8'>
          <div className='min-w-[376px] flex-1'>
            <div className='flex w-full max-w-[632px] flex-col gap-6 rounded-xl border-2 border-gray-200 p-6 dark:border-navy-160'>
              <div className='flex gap-4'>
                <Skeleton className='h-40 flex-1 rounded-xl' />
                <Skeleton className='h-40 flex-1 rounded-xl' />
              </div>
              <div className='space-y-4'>
                <Skeleton className='h-5 w-32' />
                <Skeleton className='h-12 w-full rounded-lg' />
                <div className='grid grid-cols-2 gap-4'>
                  <Skeleton className='h-12 rounded-lg' />
                  <Skeleton className='h-12 rounded-lg' />
                </div>
                <Skeleton className='h-12 w-full rounded-lg' />
              </div>
              <Skeleton className='h-12 w-full rounded-full' />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
